
import { Utils } from "../../utils";
import { i18nManager } from "../../i18n";
import StatefulLink from "./StatefulLink";
import infoImage from "../../assets/images/info.svg";
import { PopoverManageRef, Classname, Popover, ObjectHelper, Subscriber, DateHelper } from "@rivtn/noseur";
import { BaseService, HostService, INotification, ITeamInvite, SanitizedResponse, TeamService } from "../../services";
import { Siren } from "./ConfirmDialog";

export interface NotificationGroup {
    today: INotification[];
    earlier: INotification[];
    yesterday: INotification[];
}

export const Notifier = {

    __LABELS: i18nManager.Labels.notification.action_labels,

    groupNotifications(notifications: INotification[]) {
        const groups: NotificationGroup = {
            today: [],
            earlier: [],
            yesterday: [],
        }
        for (const notification of notifications) {
            const createdAt = new Date(notification.created_at);
            if (Utils.dateIsToday(createdAt)) groups.today.push(notification);
            else if (Utils.dateIsYesterday(createdAt)) groups.yesterday.push(notification);
            else groups.earlier.push(notification);
        }
        return groups;
    },

    buildNotificationActionLabel(action: { type: string }): string {
        return (Notifier.__LABELS as any)?.[action.type?.toLowerCase()] ?? action.type;
    },

    buildNotificationActions(notification: INotification) {
        return (<>
            {notification.actions?.map((action, index) => {
                return (<span key={action.type + index} className={Classname.build("link", { "base": !!action.data.base_action })}
                    onClick={() => Notifier.notificationOnClickEvent(action, notification)}>{Notifier.buildNotificationActionLabel(action)}</span>);
            })}
        </>);
    },

    normalizeNotificationMessage(message: string) {
        return ObjectHelper.resolveEnclosedValues(message, [
            {
                search: {
                    open: "**",
                    close: "**",
                },
                replace: {
                    open: "<b>",
                    close: "</b>",
                }
            }
        ]);
    },

    buildNotification(notification: INotification,
        index: number, notManager: boolean,
        largeContentNotificationsTrackerState: [{ [key: number]: boolean; }, React.Dispatch<React.SetStateAction<{ [key: number]: boolean; }>>],
        fetchNotifications: () => void, service?: { updateNotificationStatus: any }) {
        const message = Notifier.normalizeNotificationMessage(notification.message);
        const unread = notification.status === "UN_READ";
        const isLargeMessage = message.length > 110;
        const notificationItemPopOverManageRef: { current: PopoverManageRef | null } = { current: null };
        const [largeContentNotificationsTracker, setLargeContentNotificationsTracker] = largeContentNotificationsTrackerState;

        return (<div key={Utils.randomString(10)} className={Classname.build("notification", { "highlight": (service && unread) })} onClick={() => {
            if (notification.status === "UN_READ") {
                service?.updateNotificationStatus(notification.external_id).then(({ }: any) => {
                    notification.status = "READ";
                    Subscriber.report("NOTIFICATION.STATUS.UPDATE", "READ");
                }).catch(BaseService.reportError);
            }
        }}>
            <img alt={notification.title} src={notification.image_location ?? infoImage} />
            <div className="msg">
                <span className="m">
                    <span dangerouslySetInnerHTML={{ __html: message.substring(0, (!isLargeMessage || largeContentNotificationsTracker[index] ? message.length : 110)) }} />
                    {(isLargeMessage) && (<span style={{ marginLeft: 1, color: "var(--mainColor)" }} onClick={() => {
                        if (!isLargeMessage) return;
                        setLargeContentNotificationsTracker({ ...largeContentNotificationsTracker, [index]: !largeContentNotificationsTracker[index] });
                    }}> <span dangerouslySetInnerHTML={{
                        __html: (largeContentNotificationsTracker[index]
                            ? i18nManager.Labels.common.view_less
                            : i18nManager.Labels.common.view_more)
                    }} className="link" /></span>)}
                </span>
                <span className="t">{Notifier.notificationDateFineValue(notification.created_at)}</span>
            </div>
            {(service) && (<div className="ctrl">
                <i className="fa fa-ellipsis" onClick={(e: any) => {
                    e.stopPropagation();
                    notificationItemPopOverManageRef.current?.toggle(e);
                }} />
                {unread && (<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="7.5" cy="7.5" r="7" stroke="#FF7171" />
                    <circle cx="7.5" cy="7.5" r="4.5" fill="#FF7171" />
                </svg>)}
            </div>)}

            {(service) && (<Popover dismissOnClick manageRef={notificationItemPopOverManageRef} className={"noseur-popover-b np-b-min"} style={{ minWidth: 90, marginTop: 55 }}>
                {Notifier.buildNotificationActions(notification)}
                {(!notManager) && (<>
                    <StatefulLink label={unread ? i18nManager.Labels.compound.top_navbar.popover.notification.mark_as_read : i18nManager.Labels.compound.top_navbar.popover.notification.mark_as_unread}
                        onClick={async (done: () => Promise<void>, e: any) => {
                            e.stopPropagation();
                            service.updateNotificationStatus(notification.external_id, (unread ? "READ" : "UN_READ")).then(({ }: any) => {
                                notification.status = (unread ? "READ" : "UN_READ");
                                Subscriber.report("NOTIFICATION.STATUS.UPDATE", unread ? "READ" : "UN_READ");
                            }).catch(BaseService.reportError).finally(done);
                        }} />
                    <StatefulLink className="danger" label={i18nManager.Labels.compound.top_navbar.popover.notification.delete} onClick={async (done: () => Promise<void>, e: any) => {
                        e.stopPropagation();
                        service.updateNotificationStatus(notification.external_id, "DELETED").then(({ }: any) => {
                            notification.status = "DELETED";
                            Subscriber.report("NOTIFICATION.STATUS.UPDATE", "DELETED");
                            fetchNotifications?.();
                        }).catch(BaseService.reportError).finally(done);
                    }} />
                </>)}
            </Popover>)}
        </div>);
    },

    buildPopoverNotification(service: { updateNotificationStatus: any }, notification: INotification, fetchNotifications: () => void) {
        const message = Notifier.normalizeNotificationMessage(notification.message);
        const notificationItemPopOverManageRef: { current: PopoverManageRef | null } = { current: null };

        if (notification.status === "DELETED") return null;

        return (<div key={Utils.randomString(10)} className={Classname.build({ "highlight": notification.status === "UN_READ" })} onClick={() => {
            if (notification.status === "UN_READ") {
                service.updateNotificationStatus(notification.external_id).then(({ }: any) => {
                    notification.status = "READ";
                    Subscriber.report("NOTIFICATION.STATUS.UPDATE", "READ");
                }).catch(BaseService.reportError);
            }
        }}>
            <img src={notification.image_location ?? infoImage} alt={notification.title} />
            <div>
                <span dangerouslySetInnerHTML={{ __html: message }} />
                <span className="when">{Notifier.notificationDateFineValue(notification.created_at, true)}</span>
            </div>
            <i className="fa fa-ellipsis" onClick={(e: any) => {
                e.stopPropagation();
                notificationItemPopOverManageRef.current?.toggle(e);
            }} />

            <Popover dismissOnClick manageRef={notificationItemPopOverManageRef} className={"noseur-popover-b np-b-min"} style={{ minWidth: 90, marginTop: 55 }}>
                {Notifier.buildNotificationActions(notification)}
                <StatefulLink className="danger" label={i18nManager.Labels.compound.top_navbar.popover.notification.delete} onClick={async (done: () => Promise<void>, e: any) => {
                    e.stopPropagation();
                    service.updateNotificationStatus(notification.external_id, "DELETED").then(({ }: any) => {
                        notification.status = "DELETED";
                        Subscriber.report("NOTIFICATION.STATUS.UPDATE", "DELETED");
                        fetchNotifications?.();
                    }).catch(BaseService.reportError).finally(done);
                }} />
            </Popover>
        </div>);
    },

    notificationDateFineValue(createdAtStr: string, yesterdayIsFine: boolean = false) {
        const createdAt = new Date(createdAtStr);
        if (Utils.dateIsToday(createdAt) || (yesterdayIsFine && Utils.dateIsYesterday(createdAt))) return DateHelper.dateToFineValue(createdAt);
        return Utils.formatToFineDate3(createdAtStr);
    },

    notificationOnClickEvent(action: { type: string; event: string; data: any; }, notification: INotification) {
        if (action.type === "MESSAGE") {
            const queries = new URLSearchParams(action.data);
            window.open(`/messages?=${queries}`);
        } if (action.type === "VIEW_MEMBER_PROFILE") {
            window.open(Utils.buildPublicUrl("PROFILE", action.data.member_username));
        } else if (action.type === "OPEN_SUPPORT_TICKET") {
            const queries = new URLSearchParams(action.data);
            window.open(`https://support.rivtn.com/omnehost/tickets/create?=${queries}`, "_blank");
        } else if (action.type === "CANCEL_TEAM_INVITE") {
            Subscriber.report("TEAM.CANCEL_TEAM", action.data.team_invite_id);
        } else if (action.type === "ACCEPT_TEAM_INVITE" || action.type === "REJECT_TEAM_INVITE") {
            TeamService.getInstance().acceptOrRejectTeamInvite(action.data.team_invite_id, (action.type === "ACCEPT_TEAM_INVITE")).then(({ apiResponse }: SanitizedResponse<any>) => {
                Siren.alert(apiResponse.message);
                Subscriber.report("TEAM.FETCH");
            }).catch(BaseService.reportError);
        } else if (action.type === "VIEW_JOIN_CODE") {
            TeamService.getInstance().getSingleTeamInvite(action.data.team_invite_id).then(({ sanitized }: SanitizedResponse<ITeamInvite>) => {
                Subscriber.report("TEAM.VIEW_JOIN_CODE", sanitized);
            }).catch(BaseService.reportError);
        } else if (action.type === "ACCEPT_HOST_MANAGER_INVITE" || action.type === "REJECT_HOST_MANAGER_INVITE") {
            HostService.getInstance().acceptOrRejectHostInvite(action.data.host_invite_id, (action.type === "ACCEPT_HOST_MANAGER_INVITE")).then(({ apiResponse }: SanitizedResponse<any>) => {
                Siren.alert(apiResponse.message);
                Subscriber.report("HOST.FETCH");
            }).catch(BaseService.reportError);
        }
    }

};